/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import { useTranslation } from '../../i18n'
import XText from 'v2/components/XText'
import styles from './style.module.scss'
import { XTEXT_SIZE } from 'v2/styles/constants'
import { desktopThreshold } from 'components/BaseComponents/constants'
import { useMyRecentPaymentsQuery, PaymentStatus, useMyScheduledPaymentsQuery } from 'types/graphql'
import { useAuthenticatedUser } from 'v2/context/user'
import ConditionalRender from 'utils/conditionalRender'
import XColors from 'v2/styles/colors'

const ITEMS_PER_PAGE = 10

const ToggleablePaymentsTable: React.FC = () => {
    const { t } = useTranslation(window.LANG_NS)
    const [pageIndex, setPageIndex] = useState<number>(0)
    const [tableState, setTableState] = useState('recentPayments')
    const { data: recentPayments } = useMyRecentPaymentsQuery()
    const { transferMethods } = useAuthenticatedUser()
    const { data: futurePayments } = useMyScheduledPaymentsQuery()
    const [isMobile, setIsMobile] = useState<boolean>(false)

    const CATEGORIES = {
        recentPayments: t('home_recent_payments'),
        futurePayments: t('home_future_payments'),
    }

    const categories = useMemo(() => {
        return CATEGORIES
    }, [t])

    const tableData = useMemo(() => {
        return tableState === 'recentPayments'
            ? recentPayments?.myRecentPayments || []
            : futurePayments?.myUpcomingScheduledPayments || []
    }, [tableState, futurePayments, recentPayments])

    const totalPages = Math.ceil(tableData.length / ITEMS_PER_PAGE)

    const paginatedTableData = useMemo(() => {
        const startIndex = pageIndex * ITEMS_PER_PAGE
        const endIndex = startIndex + ITEMS_PER_PAGE
        return tableData.slice(startIndex, endIndex)
    }, [tableData, pageIndex, tableState, futurePayments, recentPayments])

    const categoryHandler = (category) => {
        setTableState(category)
    }

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    const renderTable = ({ data }) => {
        return (
            <table className={styles.table}>
                {data?.length > 0 && (
                    <thead className={styles.table__header}>
                        <tr>
                            <th>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.midGray}>
                                    {t('home_table_header_amount')}
                                </XText>
                            </th>
                            <th>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.midGray}>
                                    {t('home_table_header_status')}
                                </XText>
                            </th>
                            <th>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.midGray}>
                                    {t('home_table_header_date')}
                                </XText>
                            </th>
                            <th>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.midGray}>
                                    {t('home_table_header_payment_method')}
                                </XText>
                            </th>
                        </tr>
                    </thead>
                )}
                <tbody className={styles.table__body}>
                    {!data?.length && (
                        <div style={{ textAlign: 'center', margin: '0 auto' }}>
                            <ConditionalRender if={tableState === 'recentPayments'}>
                                <XText color={XColors.darkGray} size={XTEXT_SIZE.LARGE_UNBOLDED}>
                                    {t('home_recent_payments_empty_title')}
                                </XText>
                            </ConditionalRender>
                            <ConditionalRender if={tableState !== 'recentPayments'}>
                                <XText color={XColors.darkGray} size={XTEXT_SIZE.LARGE_UNBOLDED}>
                                    {t('no_upcoming_payments')}
                                </XText>
                            </ConditionalRender>
                        </div>
                    )}
                    {data?.map((payment, idx) => (
                        <tr key={idx} className={styles.table__row}>
                            <td>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                    ${payment.amount}
                                </XText>
                            </td>
                            <td>
                                <ConditionalRender if={payment.status === PaymentStatus.Completed}>
                                    <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                        {t('home_recent_payments_delivered')}
                                    </XText>
                                </ConditionalRender>
                                <ConditionalRender if={payment.status === PaymentStatus.InProgress}>
                                    <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                        {t('home_recent_payments_in_progress')}
                                    </XText>
                                </ConditionalRender>
                                <ConditionalRender
                                    if={
                                        ![PaymentStatus.InProgress, PaymentStatus.Completed].includes(payment.status) &&
                                        tableState === 'recentPayments'
                                    }
                                >
                                    <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                        {t('home_recent_payments_failed')}
                                    </XText>
                                </ConditionalRender>
                                <ConditionalRender if={tableState === 'futurePayments'}>
                                    <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                        Upcoming
                                    </XText>
                                </ConditionalRender>
                            </td>
                            <td>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray}>
                                    <ConditionalRender if={tableState === 'recentPayments'}>
                                        {moment(payment?.createdAt)
                                            .tz(window.ORGANIZATION_CONFIGS.timezone)
                                            .format('MMMM D, YYYY')}
                                    </ConditionalRender>
                                    <ConditionalRender if={tableState === 'futurePayments'}>
                                        {moment(payment?.scheduledDate)
                                            .tz(window.ORGANIZATION_CONFIGS.timezone)
                                            .format('MMMM D, YYYY')}
                                    </ConditionalRender>
                                </XText>
                            </td>
                            <td>
                                <XText size={XTEXT_SIZE.MEDIUM_BIGGER} color={XColors.darkGray} ellipsis={true}>
                                    {payment?.transferMethod?.oneLiner ||
                                        transferMethods.find((itm) => itm.isCurrentMethod).oneLiner}
                                </XText>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )
    }

    return (
        <div className={styles.table__wrapper}>
            <div className={styles.table__categories}>
                {Object.keys(categories).map((category, index) => (
                    <div key={index} className={styles.table__button_container}>
                        <XText
                            color={tableState === category ? XColors.primaryColor : XColors.disabledGray}
                            size={isMobile ? XTEXT_SIZE.MEDIUM_BIGGER_BOLDED : XTEXT_SIZE.LARGE}
                            onClick={() => {
                                categoryHandler(category)
                            }}
                        >
                            {t(categories[category])}
                        </XText>
                    </div>
                ))}
            </div>
            {renderTable({ data: paginatedTableData })}
            {paginatedTableData?.length > 0 && (
                <div className={styles.table__navigation}>
                    <button disabled={pageIndex === 0} onClick={() => setPageIndex((prev) => prev - 1)}>
                        Prev
                    </button>
                    <div className={styles.table__navigation_text}>
                        <XText color={XColors.black}>
                            Page {pageIndex + 1} of {totalPages}
                        </XText>
                    </div>
                    <button disabled={pageIndex === totalPages - 1} onClick={() => setPageIndex((prev) => prev + 1)}>
                        Next
                    </button>
                </div>
            )}
        </div>
    )
}

export default ToggleablePaymentsTable
